import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Grid, Paper, Divider, Stack } from "@mui/material";
import {
    WorkOutline,
    PeopleOutline,
    BusinessCenter,
    Speed,
    Security,
    TrendingUp,
    CheckCircleOutline
} from "@mui/icons-material";
import Homenavbar from "./Homenavbar";
import Homefooter from "./Homefooter";
import DemoRequestDialog from "./Components/DemoRequestDialog";

const AboutUs = () => {
    const [demoDialogOpen, setDemoDialogOpen] = useState(false);
    const [activeLink, setActiveLink] = useState('About Us');

    useEffect(() => {
        window.scrollTo(0, 0);

        // Update Meta Tags
        document.title = "About Us | JobsnProfiles - Revolutionizing the U.S. IT Staffing Ecosystem";
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute("content", "Learn more about JobsnProfiles, the dedicated Bench Sales portal designed to connect recruiters, employers, and consultants in the US IT staffing industry.");
        }
    }, []);

    const handleDemoOpen = () => setDemoDialogOpen(true);
    const handleDemoClose = () => setDemoDialogOpen(false);

    return (
        <Box sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
            <Homenavbar
                onRequestDemo={handleDemoOpen}
                activeLink={activeLink}
                setActiveLink={setActiveLink}
            />

            {/* Hero Section */}
            <Box
                sx={{
                    background: "linear-gradient(135deg, #1d3577 0%, #8f8854 100%)",
                    color: "white",
                    py: { xs: 10, md: 15 },
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            mb: 3,
                            letterSpacing: "-0.02em",
                            fontSize: { xs: "2.5rem", md: "3.5rem" }
                        }}
                    >
                        About <Box component="span" sx={{ color: "#FFFFFF" }}>Jobs</Box><Box component="span" sx={{ color: "#FFFFFF" }}>'n'</Box><Box component="span" sx={{ color: "#FF6B00" }}>Profiles</Box>
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            color: "#94A3B8",
                            lineHeight: 1.6,
                            fontWeight: 400,
                            maxWidth: "800px",
                            mx: "auto"
                        }}
                    >
                        The dedicated Bench Sales Portal designed to revolutionize the U.S. IT staffing ecosystem.
                    </Typography>
                </Container>

                {/* Decorative Elements */}
                <Box sx={{
                    position: "absolute",
                    top: -100,
                    right: -100,
                    width: 300,
                    height: 300,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(255,107,0,0.1) 0%, transparent 70%)"
                }} />
            </Box>

            <Container maxWidth="lg" sx={{ mt: -8, pb: 1, position: "relative", zIndex: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 4, md: 8 },
                        borderRadius: "24px",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.05)",
                        backgroundColor: "#FFFFFF"
                    }}
                >
                    <Grid container spacing={6}>
                        <Grid item xs={12}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1E293B", mb: 3 }}>
                                Empowering the U.S. IT Staffing Community
                            </Typography>
                            <Typography paragraph sx={{ color: "#475569", fontSize: "1.1rem", lineHeight: 1.8, mb: 4 }}>
                                <Box component="span" sx={{ color: "#6C2F26", fontWeight: 700 }}>Jobs</Box><Box component="span" sx={{ color: "#6C2F26", fontWeight: 700 }}>'n'</Box><Box component="span" sx={{ color: "#FF6B00", fontWeight: 700 }}>Profiles</Box> Bench Sales (BS) Portal is a dedicated platform designed to connect bench sales recruiters, IT consultants, employers, and staffing agencies across the United States. Our goal is to simplify the bench sales process by bringing the right talent and the right opportunities together on one trusted and secure platform built specifically for the U.S. IT staffing ecosystem.
                            </Typography>
                            <Typography paragraph sx={{ color: "#475569", fontSize: "1.1rem", lineHeight: 1.8 }}>
                                We understand the fast-paced and competitive nature of the U.S. IT staffing market, where speed, accuracy, and trust play a critical role. Recruiters must quickly market available consultants, while employers and implementation partners need immediate access to qualified, ready-to-deploy talent. That’s why our platform is designed to help recruiters efficiently discover opportunities, promote active bench resources, post bench sales jobs, and collaborate seamlessly with employers and vendors.
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 4 }} />
                        </Grid>

                        {/* What We Do */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1E293B", mb: 4, display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Speed sx={{ color: "#FF6B00" }} /> What We Do
                            </Typography>
                            <Stack spacing={3}>
                                {[
                                    { text: "Recruiters to showcase and manage active bench consultant profiles with ease", icon: <PeopleOutline /> },
                                    { text: "Employers and vendors to find skilled IT professionals available for immediate deployment", icon: <BusinessCenter /> },
                                    { text: "Staffing firms to post and promote bench sales and contract job opportunities", icon: <WorkOutline /> },
                                    { text: "Faster and more direct connections between consultants and hiring decision-makers", icon: <TrendingUp /> }
                                ].map((item, idx) => (
                                    <Box key={idx} sx={{ display: "flex", gap: 2 }}>
                                        <Box sx={{ color: "#FF6B00", mt: 0.5 }}>{item.icon}</Box>
                                        <Typography sx={{ color: "#475569", lineHeight: 1.6 }}>{item.text}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>

                        {/* Why Choose Us */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1E293B", mb: 4, display: "flex", alignItems: "center", gap: 1.5 }}>
                                <CheckCircleOutline sx={{ color: "#FF6B00" }} /> Why Choose <Box component="span" sx={{ color: "#6C2F26", fontWeight: 700 }}>Jobs</Box><Box component="span" sx={{ color: "#6C2F26", fontWeight: 700 }}>'n'</Box><Box component="span" sx={{ color: "#FF6B00", fontWeight: 700 }}>Profiles</Box>
                            </Typography>
                            <Grid container spacing={3}>
                                {[
                                    { title: "USA-Focused", desc: "Dedicated for IT staffing and bench sales", icon: "🇺🇸" },
                                    { title: "Privacy Controls", desc: "Protect consultant and recruiter information", icon: "🔒" },
                                    { title: "Fast Management", desc: "Fast & Easy Profile Management", icon: "⚡" },
                                    { title: "Direct Connections", desc: "Between recruiters and employers", icon: "🤝" }
                                ].map((item, idx) => (
                                    <Grid item xs={12} sm={6} key={idx}>
                                        <Box sx={{ p: 2, borderRadius: "16px", backgroundColor: "#F8FAFC", height: "100%", border: "1px solid #E2E8F0" }}>
                                            <Typography variant="h3" sx={{ mb: 1 }}>{item.icon}</Typography>
                                            <Typography sx={{ fontWeight: 600, color: "#1E293B", mb: 0.5 }}>{item.title}</Typography>
                                            <Typography variant="body2" sx={{ color: "#64748B" }}>{item.desc}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 4 }} />
                        </Grid>

                        {/* Who Can Use This Platform */}
                        <Grid item xs={12}>
                            <Typography variant="h5" sx={{ fontWeight: 500, color: "#1E293B", mb: 4, textAlign: "center" }}>
                                Who Can Use This Platform
                            </Typography>
                            <Grid container spacing={3} justifyContent="center">
                                {[
                                    "Bench Sales Recruiters",
                                    "IT Staffing & Consulting Companies",
                                    "Employers & Implementation Partners",
                                    "Independent IT Consultants"
                                ].map((role, idx) => (
                                    <Grid item xs={12} sm={6} md={3} key={idx}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                textAlign: "center",
                                                borderRadius: "16px",
                                                backgroundColor: "#F1F5F9",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "transform 0.2s",
                                                "&:hover": { transform: "translateY(-5px)" }
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 600, color: "#334155" }}>{role}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{
                                mt: 6,
                                p: { xs: 4, md: 6 },
                                borderRadius: "24px",
                                background: "linear-gradient(135deg, #FF6B00 0%, #FF8533 100%)",
                                color: "white",
                                textAlign: "center"
                            }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Our Mission</Typography>
                                <Typography sx={{ fontSize: "1.2rem", lineHeight: 1.8, maxWidth: "900px", mx: "auto" }}>
                                    Our mission is to empower recruiters and employers by providing a reliable platform that bridges the gap between available IT talent and real hiring needs in the U.S. market. We aim to strengthen collaboration, improve hiring efficiency, and deliver consistent value to the IT staffing community through technology-driven solutions.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>

            <Homefooter onRequestDemo={handleDemoOpen} />
            <DemoRequestDialog open={demoDialogOpen} onClose={handleDemoClose} />
        </Box>
    );
};

export default AboutUs;
